import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, FileSpreadsheet } from "lucide-react";
import * as ownerApi from "../../api/payroll/payrollReportApi";
import * as portalApi from "../../api/payroll/payrollReportPortalApi";
import { Card, CardContent, Alert, Table, EmptyState, Spinner } from "../../components/ui";
import { useToast } from "../../context/ToastContext";

export default function StatutoryReport({ isPortal = false }) {
  const api = isPortal ? portalApi : ownerApi;
  const { runId } = useParams();
  const base = isPortal ? "/staff-panel/payroll-runs" : "/payroll-runs";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const toast = useToast();

  useEffect(() => {
    (async () => {
      try {
        const res = await api.getStatutoryReport(runId);
        setData(res?.data || res);
      } catch (err) {
        toast.error(err.response?.data?.message || "Failed to load");
      } finally {
        setLoading(false);
      }
    })();
  }, [runId, isPortal, api]);

  if (loading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Spinner size="lg" label="Loading statutory report…" />
      </div>
    );
  }

  const totals = data?.totals || {};

  // ---- Presentation-only derivations below (no data/logic changes) ----

  const kpiCards = [
    { label: "PF Employee", value: totals.pfEmployee },
    { label: "PF Employer", value: totals.pfEmployer },
    { label: "ESI Employee", value: totals.esiEmployee },
    { label: "ESI Employer", value: totals.esiEmployer },
    { label: "Prof. Tax", value: totals.professionalTax },
    { label: "TDS", value: totals.tds },
  ];

  const tableRows = (data?.rows || []).map((r, i) => ({ ...r, id: r.staffCode || i }));

  const columns = [
    { key: "staffCode", header: "Code" },
    {
      key: "name",
      header: "Name",
      nowrap: true,
      render: (r) => <span className="font-medium text-neutral-900 dark:text-neutral-100">{r.name}</span>,
    },
    { key: "grossPay", header: "Gross", align: "right", render: (r) => Number(r.grossPay || 0).toLocaleString() },
    { key: "pfEmployee", header: "PF Emp", align: "right", render: (r) => Number(r.pfEmployee || 0).toLocaleString() },
    { key: "pfEmployer", header: "PF Er", align: "right", render: (r) => Number(r.pfEmployer || 0).toLocaleString() },
    { key: "esiEmployee", header: "ESI Emp", align: "right", render: (r) => Number(r.esiEmployee || 0).toLocaleString() },
    { key: "esiEmployer", header: "ESI Er", align: "right", render: (r) => Number(r.esiEmployer || 0).toLocaleString() },
    { key: "professionalTax", header: "PT", align: "right", render: (r) => Number(r.professionalTax || 0).toLocaleString() },
    { key: "tds", header: "TDS", align: "right", render: (r) => Number(r.tds || 0).toLocaleString() },
  ];

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-2 border-b border-neutral-200 dark:border-neutral-800 pb-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-100 sm:text-2xl">
          Statutory Report — {data?.run?.month}/{data?.run?.year}
        </h1>
        <Link
          to={`${base}/${runId}`}
          className="inline-flex items-center gap-1 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" /> Back
        </Link>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
        {kpiCards.map((k) => (
          <Card key={k.label}>
            <CardContent className="p-3">
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-0.5">{k.label}</p>
              <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
                ₹{Number(k.value || 0).toLocaleString()}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardContent className="p-0">
          <Table
            columns={columns}
            data={tableRows}
            density="compact"
            emptyState={<EmptyState icon={FileSpreadsheet} title="No statutory records found" />}
          />
        </CardContent>
      </Card>
    </div>
  );
}